import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { cn, StatusDot, token } from '@bp-files/ui';
import { APP_COPY } from '@/copy/app.copy';
import { BrandMark } from '@/components/BrandMark';
import type { Me } from '@/lib/api';
import { SIGN_IN_LANDING_COPY } from './signInLanding.copy';
import { NameForm } from './NameForm';
import { PasscodeForm } from './PasscodeForm';
import { offerFor, showsPasscodeLink, type Failure, type Offer } from './signInOffer';

/**
 * The sign-in landing page — `apps/web`'s only anonymous surface, and since
 * ADR-0051 its only authentication surface of any kind.
 *
 * **Why not a shared gate.** `@bp-files/ui` used to carry one, mounted by both
 * apps. The brief here was to carry marketing content that names this product
 * specifically ("Isolated by agency", a file-transfer ledger) and means nothing
 * on a console whose visitor belongs to no agency at all. Reshaping a shared
 * component to carry it would have put an `if (isConsole)` inside the one
 * component both apps mount before either has a router — so each app owns its
 * landing page, and the shared gate is gone.
 *
 * **It asks nothing of storage, and that is ADR-0083.** The page used to read a
 * `localStorage` flag to decide whether a visitor had a passkey on this browser.
 * WebAuthn exposes no way to ask that question — credential existence is the one
 * fact the spec refuses to leak, because a page that could probe it could probe
 * whether you have an account here — so the flag was a guess maintained by hand
 * across four modules. iOS clears that storage on a schedule of its own, so the
 * guess came back wrong roughly daily for the population it existed to serve: a
 * member who had enrolled on their iPhone was offered the envelope every morning
 * and typed eight digits to reach a credential that was there the whole time.
 * The page offers the passkey unconditionally now, with the code one click below
 * it, and holds no opinion about the browser at all.
 *
 * **One control, and it is the whole of sign-in** (ADR-0051). There used to be
 * three surfaces: this one chose between a passcode redirect and an assertion,
 * `SecureSignInPrompt` was a routeless interstitial nagging a session holder to
 * enrol, and `/settings/passkeys` was both an account screen and the place
 * `useSteering` sent a restricted session on arrival. Two of those moves
 * happened without anybody clicking anything that said so, and the third — an
 * "upgrade" control on the account screen — had never worked in a deployed
 * build. All of it collapses here. `/settings/passkeys` is management now:
 * list, rename, remove, and the shared-workstation enrolment this page does not
 * ask about. Nothing navigates to it.
 *
 * **The decision tree is `signInOffer.ts`, and it is a pure function in a file
 * of its own.** That is deliberate after four rewrites of this page: everything
 * deciding what the button does has no effects, no storage, and no network, so
 * it can be read in one screenful and tested as a table. What is left here is
 * the acts themselves and the rendering.
 *
 * **The invariant that replaced the steering: `onReady` is never called with a
 * restricted `me`.** SYS-10 restricts a session that authenticated with a
 * passcode while the account already holds a passkey, and `withAuth` refuses
 * every surface such a session would land on — so handing one over renders an
 * app in which every request answers 403. It used to be redirected to the
 * account screen; it is now simply held here until it has enrolled *and*
 * asserted, which is what SYS-10 asks for and what USR-11a calls rung 1.
 *
 * **A failed assertion still never navigates, and that rule survived the
 * rewrite.** Cancelling a prompt and holding no credential on this device raise
 * the same `NotAllowedError`, so this page cannot tell the accident from the
 * dead end. What changed is the recovery: instead of revealing a second control
 * underneath, the primary button stops offering the passkey and offers the next
 * credential — the passcode for a visitor, a replacement passkey for a
 * restricted session, which is the one population the passcode would loop.
 *
 * **`resolve` reports; it never moves anyone.** It answers `GET /me` rather than
 * MSAL (`lib/bpSession.ts` says why the cookie is the better question). The
 * hand-over is this file's, and it is a hand-over rather than a navigation:
 * `onReady` mounts the app over whatever address the browser is already at.
 *
 * **The page stops nobody it has nothing to ask** (ADR-0104). `'continue'` is an
 * answer, not a control: when the offer is the door, this page goes through it.
 * The rule it replaces — nothing moves without a click — was written for a gate
 * that only ever asked "who are you"; what it produced once the landing page
 * owned the whole of sign-in was a greeting and a button on **every reload**,
 * between a member and the space link they had just opened. `apps/admin`'s
 * gate had already reached the same conclusion from the other side and for the
 * same reason: on an F5 nobody is watching.
 *
 * The click survives everywhere it is doing something — it starts a ceremony,
 * retries a check, defers a nudge, or submits a form. What it no longer does is
 * acknowledge a session the visitor never asked about.
 *
 * **No artificial hold.** The shared gate floors its spinner at 1.5s (ADR-0034)
 * so a fast answer and a slow one look the same. That floor makes a *spinner*
 * read as deliberate; a button whose own label names the state has no flicker to
 * hide, so this page shows whatever the check actually takes.
 */

/** Where the page is, as opposed to what the button offers. */
type Phase =
  /** The check is in flight. Its answer is the only thing this page waits for. */
  | { kind: 'checking' }
  /** Answered, and awaiting a click. `failure` is what the last click did. */
  | { kind: 'ready'; failure?: Failure }
  /** Clicked `passkey` — awaiting the authenticator, then `/me`. */
  | { kind: 'asserting' }
  /** Clicked `enrol` — awaiting the authenticator. A different dialog, hence a different word. */
  | { kind: 'enrolling' }
  /** We could not find out. The button retries the check, not the sign-in. */
  | { kind: 'failed' };

export interface SignInLandingProps {
  /**
   * Who the caller is, or `undefined` when nobody is signed in. Never
   * navigates. `lib/bpSession`'s `resolveSession`.
   *
   * **A rejection is not "signed out"** — it is "we could not tell", and it puts
   * this page in `failed` with a retry rather than offering a sign-in that
   * cannot fix an outage.
   *
   * **One wait, since M11.** There used to be a second — `finishing` — for a tab
   * coming back from a Microsoft page with five acts left to perform. The
   * passcode never leaves the tab now, so the only wait is one `GET /me`.
   */
  readonly resolve: () => Promise<Me | undefined>;
  /**
   * Whether this build can complete a passcode at all (SYS-7).
   *
   * **The passcode is three fields on this page now, not a redirect**, so the
   * question is no longer "was a function supplied" but "can the session it
   * mints survive". A build serving the SPA and the API from two origins can
   * hold no cookie, so the API would set one the browser discards and the
   * person would land back here having read their mail and typed eight digits.
   * `Bootstrap.tsx` reads `canHoldSession`, the same value that gates the
   * passkey, because it is the same fact.
   *
   * Since ADR-0059 a two-origin build can answer yes anyway, by carrying the
   * handle itself — which changes what `canHoldSession` consults and nothing
   * about what this prop means.
   */
  readonly canPasscode?: boolean;
  /**
   * One gesture, one session (SYS-8). `lib/passkeyDriver`'s `signInWithPasskey`.
   *
   * **Optional, and absent means no click ever attempts one.** That is what lets
   * a build which cannot complete an assertion (`Bootstrap.tsx` withholds it when
   * the API is not same-origin) behave exactly as it did before passkeys existed:
   * `offerFor` reads its absence as `canAssert: false` and the page opens on the
   * passcode field.
   */
  readonly signInWithPasskey?: () => Promise<unknown>;
  /** Creates a credential (USR-11). `lib/passkeyDriver`'s `enrollPasskey`. */
  readonly enroll: (input: { label: string; sharedDevice: boolean }) => Promise<unknown>;
  /**
   * Whether this browser can perform a WebAuthn ceremony at all.
   * `@bp-files/client`'s `webAuthnSupported` — synchronous, and importing no
   * library, so asking costs an anonymous visitor nothing.
   */
  readonly webAuthnSupported: boolean;
  /**
   * Whether this device has a built-in authenticator.
   * `lib/passkeyDriver`'s `platformAuthenticatorAvailable`.
   *
   * **It gates enrolment and nothing else.** Asserting works fine without one —
   * a security key does it, and so does a phone over the hybrid transport — so
   * gating the passkey *button* on this would hide the control from precisely
   * the desktops that need it. `webAuthnSupported` is the question that gates
   * the path; this is the question that gates creating a credential, because
   * this page enrols in the own-device shape.
   */
  readonly platformAvailable: () => Promise<boolean>;
  /** Whether the enrolment nudge has already been waved off this sitting. */
  readonly enrolmentDeferred?: boolean;
  /** Wave the nudge off. `lib/enrolmentDeferral`'s `deferEnrolment`. */
  readonly onDeferEnrolment: () => void;
  /** Hand over to the app, seeded with the `/me` the check already loaded. */
  readonly onReady: (me: Me | undefined) => void;
}

export function SignInLanding({
  resolve,
  canPasscode = true,
  signInWithPasskey,
  enroll,
  webAuthnSupported,
  platformAvailable,
  enrolmentDeferred = false,
  onDeferEnrolment,
  onReady,
}: SignInLandingProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'checking' });
  const [me, setMe] = useState<Me | undefined>(undefined);
  /** `undefined` until the probe answers. Only `true` offers enrolment. */
  const [platform, setPlatform] = useState<boolean | undefined>(undefined);
  const [deferred, setDeferred] = useState(enrolmentDeferred);
  /**
   * Somebody took the link (ADR-0083). One-way for the life of the page: there
   * is no control back to the passkey, because `PasscodeForm` is a conversation
   * and a way out of its middle would be a second primary control. A reload is
   * the way back, and it costs nothing now that no storage remembers anything.
   */
  const [passcodeRequested, setPasscodeRequested] = useState(false);
  const started = useRef(false);
  const leaving = useRef(false);
  const focusRef = useRef<HTMLButtonElement>(null);

  const check = useCallback(async (): Promise<void> => {
    setPhase({ kind: 'checking' });

    /*
      Started beside the check rather than awaited with it: it only decides
      whether *enrolment* is offered, and every branch that could offer it needs
      `/me` first. Racing them means the slow one is the only one anybody waits
      for. `platformAuthenticatorAvailable` answers `false` rather than throwing,
      and the `catch` is belt-and-braces for a mock that does not.
    */
    void platformAvailable().then(setPlatform, () => {
      setPlatform(false);
    });

    try {
      const found = await resolve();
      setMe(found);
      setPhase({ kind: 'ready' });
    } catch (cause) {
      // Every way this fails looks the same to the user and different to us —
      // ADR-0004's reasoning for the API's indistinguishable 401, applied to a
      // screen. The console gets the detail; the button gets one sentence.
      console.error('Could not find out whether anybody is signed in.', cause);
      setPhase({ kind: 'failed' });
    }
  }, [resolve, platformAvailable]);

  useEffect(() => {
    // A ref latch, not a cancellation flag: StrictMode's second effect run must
    // not check twice, and a flag cleared on cleanup would let it.
    if (started.current) return;
    started.current = true;

    void check();
  }, [check]);

  useEffect(() => {
    // The button is the page's primary control — autofocus is earned here.
    if (phase.kind === 'ready' || phase.kind === 'failed') focusRef.current?.focus();
  }, [phase.kind]);

  const failure = phase.kind === 'ready' ? phase.failure : undefined;
  const offerInput = {
    me,
    passcodeRequested,
    platformAvailable: platform,
    enrolmentDeferred: deferred,
    failure,
    canAssert: webAuthnSupported && signInWithPasskey !== undefined,
    canPasscode,
  };
  const offer = offerFor(offerInput);

  /**
   * The primary button's one handler. Which act it performs is `offerFor`'s to
   * decide, not the caller's — that is what makes this page one control.
   */
  async function handlePrimary(): Promise<void> {
    // The button disables itself the instant this runs, so a second click is
    // already impossible in the ordinary case. This guard covers the one it is
    // not: two clicks inside a single frame, before React has re-rendered.
    if (leaving.current) return;

    if (phase.kind === 'failed') {
      // Retries the *check*, not the sign-in. What failed was finding out, and
      // a `/me` that answered 500 a second ago is the commonest reason to be
      // here — sending this click to Entra would ask somebody to re-authenticate
      // their way out of an outage.
      await check();
      return;
    }

    if (phase.kind !== 'ready') return;

    leaving.current = true;

    switch (offer) {
      case 'continue':
        /*
          **The fallback, not the ordinary path** (ADR-0104). The door is taken
          without a click; this case is what is left for the one state that
          cannot be — a capability probe that never answers, where the label says
          "continue" and the layout effect is still waiting on it.

          `offerFor` cannot answer `'continue'` for a restricted session, which
          is the invariant this hand-over rests on either way.
        */
        onReady(me);
        return;
      case 'passkey':
        await assert();
        return;
      case 'enrol':
        await enrol();
        return;
      case 'email':
      case 'unavailable':
      case 'locked':
        // None is a click. `email` renders the passcode form instead of a
        // button; `unavailable` and `locked` render a disabled one — so the
        // primary handler is unreachable for all three, and `leaving` must not
        // be left set.
        leaving.current = false;
        return;
    }
  }

  /**
   * Sign in with a passkey, and hand over without a reload.
   *
   * `resolve` asks `GET /me`, the assertion has just minted the cookie that
   * answers it, and the same function that decided this page's opening state
   * decides its last one.
   *
   * **The failure is recorded and nothing is written down** (ADR-0083). This
   * used to clear a `localStorage` hint so the next load would not prompt again;
   * there is no next-load memory now, and there does not need to be. A visitor
   * who has no credential dismisses one sheet and gets the field, and a visitor
   * who cancelled by accident gets the same page back tomorrow — which is the
   * right answer for both, and was not achievable while one flag served them.
   */
  async function assert(): Promise<void> {
    if (signInWithPasskey === undefined) {
      // Unreachable: `canAssert` is false without it and `offerFor` answers
      // `'passkey'` only for the restricted branch, which cannot exist in such a
      // build. Recorded as a failure anyway so that branch terminates on
      // `'enrol'` rather than on a button that does nothing.
      leaving.current = false;
      setPhase({ kind: 'ready', failure: 'assert' });
      return;
    }

    setPhase({ kind: 'asserting' });

    try {
      await signInWithPasskey();
    } catch (cause) {
      // Cancelled, no credential on this device, or refused — indistinguishable
      // to us and, in the moment, to the person. The next step is on the button.
      console.error('Could not sign in with a passkey.', cause);
      leaving.current = false;
      setPhase({ kind: 'ready', failure: 'assert' });
      return;
    }

    try {
      const signedIn = await resolve();

      /*
        **An assertion is what lifts SYS-10's restriction, and this is where we
        find out whether it did.** It should always have: the new session's
        assurance is `passkey`. If the API disagrees, the honest thing is to stay
        here with the fresh `/me` — which puts the button back on `'enrol'`, the
        next rung of USR-11a — rather than hand over an app that will 403.
      */
      if (signedIn?.restricted === true) {
        leaving.current = false;
        setMe(signedIn);
        setPhase({ kind: 'ready', failure: 'assert' });
        return;
      }

      onReady(signedIn);
    } catch (cause) {
      // The gesture worked and `/me` did not. Handing over with no seed would
      // put `SessionGate`'s own error inside an app the visitor cannot use, so
      // the failure stays on this side of the door.
      console.error('Signed in with a passkey, but could not load the account.', cause);
      leaving.current = false;
      setPhase({ kind: 'failed' });
    }
  }

  /**
   * Create a credential on this device (USR-11), in the own-device shape.
   *
   * **It does not hand over, and for a restricted session that is the point.**
   * Enrolling raises `passkeyCount`, which turns `offerFor`'s answer from
   * `'enrol'` into `'passkey'` — so SYS-10's second act is simply the next click
   * on the same button. For a full session the count going from 0 to 1 turns the
   * nudge into `'continue'`, which is the same mechanism reading the other way.
   *
   * `me` is patched locally rather than re-fetched: the count is the only field
   * that moved, `/me` has no other news, and a second round trip between two
   * halves of one ceremony is a wait with nothing behind it.
   */
  async function enrol(): Promise<void> {
    setPhase({ kind: 'enrolling' });

    try {
      await enroll({ label: SIGN_IN_LANDING_COPY.passkeyLabel, sharedDevice: false });
    } catch (cause) {
      console.error('Could not add a passkey.', cause);
      leaving.current = false;
      setPhase({ kind: 'ready', failure: 'enrol' });
      return;
    }

    leaving.current = false;
    setMe((current) =>
      current === undefined ? current : { ...current, passkeyCount: current.passkeyCount + 1 },
    );
    setPhase({ kind: 'ready' });
  }

  /**
   * "Not now", behind the guard `handlePrimary` already has.
   *
   * Not a navigation, unlike everything else on this page — it changes what
   * `offerFor` answers and nothing else, so the button becomes `'continue'`
   * under the cursor and the next click is the door.
   */
  function defer(): void {
    if (leaving.current) return;

    setDeferred(true);
    onDeferEnrolment();
    /*
      **And clear the failure with it.** The label reports the last act and the
      offer reports the next one, which is fine while they agree and a lie the
      moment they do not: deferring after a failed enrolment left the button
      reading "Could not add a passkey — try again" over an offer that was now
      the door. Waving the nudge off is an explicit "move on", so the thing being
      moved on from stops being reported.
    */
    setPhase({ kind: 'ready' });
  }

  /**
   * "Use an email code instead", behind the guard `defer` already has (ADR-0083).
   *
   * Not a navigation either — it changes what `offerFor` answers, and the next
   * render is `PasscodeForm` where the button was. Nothing is written down: this
   * is a fact about the next thirty seconds, not about the browser, which is the
   * whole difference between it and the hint it replaces.
   */
  function requestPasscode(): void {
    if (leaving.current) return;

    setPasscodeRequested(true);
  }

  /**
   * Whether the link renders, and the two phases it is withheld in.
   *
   * `showsPasscodeLink` decides the rest — see its header for why a restricted
   * session is excluded there rather than here. What is this file's business is
   * *when*: not during `checking`, where the page has not decided anything yet
   * and a way past a button that says "Checking your session…" is noise; and not
   * during `failed`, where the one control is a retry for an outage that a
   * passcode would not fix either. `asserting` keeps it, disabled with everything
   * else — the sheet is up, and a link that vanished under it would read as the
   * page changing its mind.
   */
  const passcodeLink =
    phase.kind !== 'checking' && phase.kind !== 'failed' && showsPasscodeLink(offerInput);

  /**
   * Whether a click could do anything.
   *
   * **`'unavailable'` is not a wait**, so it is excluded here rather than given
   * a phase of its own: the page has finished checking and the answer is that
   * this build has no credential to offer. A disabled control whose label says
   * why is the honest rendering; a live one that ran a ceremony and landed back
   * here is the failure this codebase has already paid for twice.
   */
  const clickable =
    (phase.kind === 'ready' || phase.kind === 'failed') &&
    offer !== 'unavailable' &&
    // USR-14: a locked account cannot enrol, so the control names the remedy
    // rather than opening an authenticator for a ceremony the API refuses.
    offer !== 'locked';
  const greeting = me !== undefined && phase.kind !== 'checking';

  /**
   * **The name step outranks the offer**, and it has to: `offerFor` would
   * answer `'enrol'` or `'continue'` for a session that has one, and both would
   * hand somebody to the app — or to a passkey prompt — still called by their
   * own email address. It is a step rather than an act, so it sits in front of
   * the credential decision rather than inside it (USR-1).
   */
  const asking = phase.kind === 'ready' && me !== undefined && me.needsName;

  /**
   * Whether the capability probe could still turn this door into a nudge.
   *
   * **The one thing between a resolved check and the app** (ADR-0104).
   * `offerFor` reads `platformAvailable === true` for the enrolment nudge, so
   * while the probe is unanswered it answers `'continue'` for a session holding
   * no credential — and handing over on that would skip USR-11's offer for
   * precisely the session it exists for, on precisely the machines slow enough
   * to make it happen.
   *
   * So the probe is awaited only where it can matter, which is what asking
   * `offerFor` the hypothetical establishes: if the answer is the door with a
   * platform authenticator too, there is nothing to wait for. That keeps the
   * common case — a reload on a session that holds a credential — free of a
   * WebAuthn round trip it does not need, and keeps a probe that never settles
   * from turning into a gate that never opens.
   */
  const nudgeUndecided =
    platform === undefined && offerFor({ ...offerInput, platformAvailable: true }) !== 'continue';

  /** The offer is the door, and there is nothing left to find out. */
  const goingIn = phase.kind === 'ready' && !asking && offer === 'continue' && !nudgeUndecided;

  /**
   * Go through it.
   *
   * **`useLayoutEffect`, and it is the only one in the repo.** `onReady` swaps
   * this page for the app in the parent, and doing that after paint means a
   * frame of "Welcome back" and a button nobody should press on every single
   * reload — the flicker version of the defect this change removes. A layout
   * effect commits the swap before the browser paints, so a reload renders the
   * app and nothing else.
   *
   * **`leaving` is the latch rather than a ref of its own.** It already means "a
   * hand-over is in flight, ignore further input", it is the guard the button's
   * own handler checks, and it is cleared by every act that fails and stays. A
   * second ref would be a second answer to one question.
   */
  useLayoutEffect(() => {
    if (!goingIn || leaving.current) return;

    leaving.current = true;
    onReady(me);
  }, [goingIn, me, onReady]);

  /**
   * The hero's body: a form when the page is asking for something typed, and
   * the one button otherwise. Never both — SYS-8's rule survives fields only
   * because there is still exactly one primary control on screen.
   */
  const form =
    phase.kind !== 'ready' ? undefined : asking && me !== undefined ? (
      <NameForm email={me.user.email} onNamed={check} />
    ) : offer === 'email' ? (
      <PasscodeForm onSignedIn={check} />
    ) : undefined;

  /**
   * What the last click did, when the thing replacing the button cannot say it.
   *
   * **A failed assertion has to remain legible** (SYS-8). When the next offer
   * was another button, its own label carried the news — "That did not work —
   * sign in with email". A form's submit button names the form's act, so the
   * news needs somewhere else to live, and this is it: one `role="alert"` line
   * above the field. Without it, dismissing a passkey sheet would silently swap
   * the control for a text input and look like a glitch.
   */
  const noticeText =
    form !== undefined && phase.kind === 'ready' && phase.failure === 'assert'
      ? SIGN_IN_LANDING_COPY.button.assertFailedEmail
      : undefined;

  const heroTitle = asking
    ? SIGN_IN_LANDING_COPY.passcode.name.title
    : greeting
      ? SIGN_IN_LANDING_COPY.title.welcome(me.user.name)
      : SIGN_IN_LANDING_COPY.title.default;

  return (
    <main aria-busy={!clickable} className="flex min-h-dvh bg-app text-body">
      <div className="mx-auto flex min-h-dvh w-full max-w-6xl flex-col px-6 md:px-11">
        <header className="flex h-20 items-center justify-between border-b border-hairline">
          <div className="flex items-center gap-3">
            <BrandMark height={18} color="bp-text" label={APP_COPY.product} />
            <span className="text-2xl font-bold tracking-tight">{APP_COPY.product}</span>
          </div>
          <div className="hidden items-center gap-2 text-sm text-muted sm:flex">
            <StatusDot tone="success" />
            {SIGN_IN_LANDING_COPY.headerStatus}
          </div>
        </header>

        <Hero
          phase={phase}
          offer={offer}
          clickable={clickable}
          focusRef={focusRef}
          title={heroTitle}
          greeting={greeting && !asking}
          onPrimary={handlePrimary}
          {...(form === undefined ? {} : { form })}
          {...(noticeText === undefined ? {} : { notice: noticeText })}
          {...(offer === 'enrol' && me?.restricted !== true ? { onDefer: defer } : {})}
          {...(passcodeLink ? { onUsePasscode: requestPasscode } : {})}
        />

        <section className="grid gap-8 border-t border-hairline py-10 sm:grid-cols-3">
          {SIGN_IN_LANDING_COPY.features.map((feature) => (
            <div key={feature.title}>
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-body">
                <span
                  aria-hidden="true"
                  className="inline-block size-1.5 shrink-0 rounded-full bg-amber"
                />
                {feature.title}
              </h3>
              <p className="m-0 text-sm leading-relaxed text-muted">{feature.body}</p>
            </div>
          ))}
        </section>

        {/*
          `text-muted`, not `text-faint`. `bp-text-faint` is the disabled and
          inactive-icon colour — a non-text token, held to 3:1 rather than 4.5:1
          — and these two lines are real 12px copy, one of which tells somebody
          who cannot get in what to do about it. It measured 2.68:1 in daylight
          (ADR-0064); `bp-text-muted` is the quiet *text* colour and clears AA in
          both palettes.
        */}
        <footer className="mt-auto flex flex-col gap-2 border-t border-hairline py-6 text-xs text-muted sm:flex-row sm:justify-between">
          <span>{SIGN_IN_LANDING_COPY.footer.help}</span>
          <span>{SIGN_IN_LANDING_COPY.footer.tag}</span>
        </footer>
      </div>
    </main>
  );
}

/**
 * One hero, single column.
 *
 * **There used to be two** — a two-column marketing hero for a visitor and a
 * focused one for a session holder — with an illustrative transfer-record panel
 * filling the second column. The panel is gone (ADR-0051), and with it the only
 * thing that made the two layouts different: what remains is one column whose
 * heading is what the product does, or a greeting where there is a name to say
 * instead.
 *
 * **The heading used to be the product name, over the tagline in body text.**
 * The page header above already carries the mark and the name, so the largest
 * type on the screen was repeating what the reader had just read, and the one
 * line saying what any of it is for was the smallest thing in the block. The
 * tagline is the heading now, and there is no second line.
 */
function Hero({
  phase,
  offer,
  clickable,
  focusRef,
  title,
  greeting,
  onPrimary,
  onDefer,
  onUsePasscode,
  form,
  notice,
}: {
  readonly phase: Phase;
  readonly offer: Offer;
  readonly clickable: boolean;
  readonly focusRef: RefObject<HTMLButtonElement | null>;
  readonly title: string;
  readonly greeting: boolean;
  readonly onPrimary: () => Promise<void>;
  /**
   * A form to render **instead of** the button, when the page is asking for
   * something typed. Never alongside it: one primary control is the rule.
   */
  readonly form?: ReactNode;
  /** Announced above the form. What the last click did, when a form replaced it. */
  readonly notice?: string;
  /** Absent unless the button is nudging a full session to enrol. */
  readonly onDefer?: (() => void) | undefined;
  /**
   * Absent unless the button is offering a visitor the passkey (ADR-0083).
   *
   * **It shares `onDefer`'s slot and its treatment on purpose.** Both are the
   * quiet way past the primary control, and a reader who has learned what a small
   * underlined line under this button means should not have to learn it twice.
   * The two are mutually exclusive by construction — `onDefer` needs `'enrol'`
   * and this needs `'passkey'` — so the slot never holds both.
   */
  readonly onUsePasscode?: (() => void) | undefined;
}) {
  /**
   * **One secondary slot, and the page never holds two.**
   *
   * Written as a single value rather than two conditional blocks so that the
   * exclusion is structural instead of coincidental: ADR-0051's rule is that this
   * page has one primary control, and the corollary a second control introduces
   * is that it has at most one way past it. Two independent `{x === undefined ?
   * null : …}` blocks would satisfy that today by luck — `'enrol'` and
   * `'passkey'` are different offers — and stop satisfying it the first time
   * somebody adds a third quiet line without noticing there was a rule.
   */
  const secondary =
    onDefer !== undefined
      ? { onClick: onDefer, label: SIGN_IN_LANDING_COPY.defer }
      : onUsePasscode !== undefined
        ? { onClick: onUsePasscode, label: SIGN_IN_LANDING_COPY.usePasscode }
        : undefined;

  return (
    <section className="flex flex-1 items-center py-16 lg:py-24">
      {/*
        Wide enough for either branch's longest line at `text-display`.
        "Secure file sharing between agencies" is ~891px unwrapped — past
        `max-w-2xl` (672px) and even `max-w-4xl` (896px, too close to call) —
        so it split across three rows with room to spare on every one of them.
        "Welcome back, Sgt. J. Angelle" is 672px, well inside this too. Only
        the heading and an error notice sit in here on the greeting branch
        (the passcode form belongs to the other one), so nothing else is
        stretched by the extra room.
      */}
      <div className={greeting ? 'max-w-4xl' : 'max-w-5xl'}>
        {/*
          And `text-balance` off with it: it splits a title into two even lines
          rather than filling the first, which is right for the tagline headline
          and the passcode headline and wrong for a greeting that now fits on
          one. A long enough display name still wraps, which is what should
          happen — `whitespace-nowrap` would overflow the container instead.

          `mb-8`, and the same on both branches — it was `mb-5` under a tagline
          paragraph that carried the remaining 32px down to the button. Only the
          greeting branch ever went straight from heading to control; both do
          now, so they space alike, and leaving `mb-5` would have pulled the
          button up under the headline on the branch that lost a paragraph.
        */}
        <h1
          className={`m-0 mb-8 text-display font-extrabold tracking-tight ${greeting ? '' : 'text-balance'}`}
        >
          {title}
        </h1>

        {notice === undefined ? null : (
          <p role="alert" className="mb-5 flex items-center gap-2 text-sm text-danger">
            <StatusDot tone="alert" />
            {notice}
          </p>
        )}

        {form ?? (
          <button
            ref={focusRef}
            type="button"
            onClick={() => {
              void onPrimary();
            }}
            disabled={!clickable}
            aria-busy={!clickable}
            aria-live="polite"
            className={buttonClassName(phase)}
            style={{ outlineColor: token('bp-blue-rail') }}
          >
            {iconFor(phase, offer)}
            {buttonLabel(phase, offer)}
          </button>
        )}

        {secondary === undefined ? null : (
          <button
            type="button"
            onClick={secondary.onClick}
            disabled={!clickable}
            className="mt-3 block rounded-md px-2 py-1.5 text-sm font-medium text-secondary underline underline-offset-4 transition-colors hover:text-body disabled:opacity-50"
            style={{ outlineColor: token('bp-blue-rail') }}
          >
            {secondary.label}
          </button>
        )}

        {greeting ? null : (
          <p className="mt-7 max-w-md text-sm text-muted">
            <strong className="font-semibold text-amber">
              {SIGN_IN_LANDING_COPY.support.lead}
            </strong>
            {SIGN_IN_LANDING_COPY.support.rest}
          </p>
        )}
      </div>
    </section>
  );
}

function buttonLabel(phase: Phase, offer: Offer): string {
  const { button } = SIGN_IN_LANDING_COPY;

  switch (phase.kind) {
    case 'checking':
      return button.checking;
    case 'asserting':
      return button.asserting;
    case 'enrolling':
      return button.enrolling;
    case 'failed':
      return button.failed;
    case 'ready':
      break;
  }

  // **Before the failure strings, because it outranks both of them** (USR-14).
  // A locked account reached this having failed an assertion, and the two
  // sentences below both name a way out that is also locked: `assertFailedEmail`
  // sends them to a passcode `rosterVerdict` refuses, and `assertFailedEnrol`
  // offers a ceremony `requireEnrollmentUnlocked` refuses. Neither is a remedy,
  // and a wrong remedy is worse than the plain one.
  if (offer === 'locked') return button.locked;

  if (phase.failure === 'enrol') return button.enrolFailed;

  // Two strings for one failure, because the way out differs: a visitor takes
  // the passcode, and a restricted session takes a replacement credential —
  // sending that one back to the passcode is the loop it just came out of.
  if (phase.failure === 'assert') {
    return offer === 'enrol' ? button.assertFailedEnrol : button.assertFailedEmail;
  }

  return button[offer];
}

/**
 * Which gesture this click is about to ask for, or nothing.
 *
 * **Absent for every wait, for the retry, and for `continue`**, because in none
 * of them is the button about a credential: it is a wait, a retry, and a door.
 * An icon there would be decoration.
 *
 * `enrol` and `passkey` share the key deliberately — both are about the same
 * credential, and the label already says which direction it is going.
 */
function iconFor(phase: Phase, offer: Offer) {
  if (phase.kind === 'asserting' || phase.kind === 'enrolling') return <KeyIcon />;
  if (phase.kind !== 'ready') return null;

  if (offer === 'passkey' || offer === 'enrol') return <KeyIcon />;
  if (offer === 'email') return <MailIcon />;

  return null;
}

/**
 * The button's own visual state, since there is no separate notification to
 * carry it. `failed` takes the danger tone rather than the solid accent — the
 * one place this page borrows the semantic-status colour instead of the brand
 * blue, on `StatusDot`'s own precedent.
 *
 * **A `ready` that is carrying a failure keeps the muted treatment**, and that
 * is not the same thing as being un-clickable. The accent button in that exact
 * position said something a moment ago and did not do it; re-rendering it
 * identically would claim nothing had happened.
 *
 * **`outline-none` plus `focus-visible:outline-2`, not the bare browser
 * default.** An unstyled focus ring renders in the user agent's own colour —
 * stark white against this page's navy, and on by default the instant this
 * button autofocuses (the effect above does that for `ready` and `failed`), not
 * only after a Tab press. `outlineColor` on the element points it at
 * `bp-blue-rail` instead, the same pairing `Select`, `DataTable`, and
 * `DetailDrawer` already use — a themed ring, not no ring: the button is still
 * this page's primary control and still needs to say so to a keyboard user.
 */
function buttonClassName(phase: Phase): string {
  const base =
    'inline-flex min-h-11 min-w-56 items-center justify-center gap-2.5 rounded-md border px-8 text-base font-semibold transition-colors outline-none focus-visible:outline-2 disabled:cursor-not-allowed';

  if (phase.kind === 'failed') {
    return cn(base, 'border-danger bg-transparent text-danger hover:bg-danger/10');
  }

  if (phase.kind === 'ready' && phase.failure === undefined) {
    return cn(
      base,
      'border-accent bg-accent-deep text-on-accent hover:bg-accent-hover hover:shadow-accent-soft',
    );
  }

  return cn(base, 'border-hairline-strong bg-elevated text-secondary opacity-80');
}

/**
 * Lucide's `key-round` and `mail`, hand-copied — this workspace's convention,
 * and the reason this page has no icon dependency for two paths.
 *
 * Both are `aria-hidden`: the label names the credential now (ADR-0051), so an
 * icon announced as "key" would be a screen reader saying it twice.
 */
function KeyIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" />
      <circle cx="16.5" cy="7.5" r=".5" fill="currentColor" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width={18}
      height={18}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}
